import {
  Button,
  Chip,
  Textarea,
  useDisclosure,
} from '@heroui/react'
import {
  useQueryKnowledgeBase,
} from 'hooks/useKnowledgeBase'
import {
  useCreateSuggestion,
  useSuggestions,
} from 'hooks/useSuggestions'
import {
  Edit3,
  FileText,
  Plus,
  Send,
  Trash2,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import SuggestionModal from '../../components/KnowledgeBases/SuggestionModal'

export default function Playground({ knowledgeBases, selectedKnowledgeBase }) {
  const [chatMessage, setChatMessage] = useState('')
  const [chatHistory, setChatHistory] = useState([])
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [suggestionData, setSuggestionData] = useState({
    question: '',
    answer: '',
    type: 'SUGGESTION',
  })

  const { isOpen: isSuggestionOpen, onOpen: onSuggestionOpen, onClose: onSuggestionClose } = useDisclosure()
  const chatContainerRef = useRef(null)

  // Auto-select the knowledge base when passed from Test button
  useEffect(() => {
    if (selectedKnowledgeBase && selectedKnowledgeBase.id !== selectedDocument?.id) {
      setSelectedDocument(selectedKnowledgeBase)
    }
  }, [selectedKnowledgeBase])

  const clearChatHistory = () => {
    setChatHistory([])
    setChatMessage('')
  }

  const { mutate: queryKnowledgeBase, isPending: isQuerying } = useQueryKnowledgeBase({
    onSuccess: ({ data }) => {
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: data || 'No response received from the knowledge base.',
      }])
    },
    onError: (error) => {
      console.error('Query error:', error)
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error while searching the knowledge base. Please try again.',
        error: true,
      }])
    },
  })

  // Auto-scroll to bottom when chat history changes or when querying
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatHistory, isQuerying])

  const handleSendMessage = () => {
    if (chatMessage.trim()) {
      // Add user message to chat history

      const messages = [
        ...chatHistory,
        {
          role: 'user',
          content: chatMessage,
        },
      ]

      setChatHistory(prev => [...prev, { role: 'user', content: chatMessage }])

      queryKnowledgeBase({
        // query: chatMessage,
        messages,
        knowledge_base_id: selectedDocument?.id,
        top_k: 5,
      })

      setChatMessage('')
    }
  }

  // Fetch suggestions for the selected document
  const { data: suggestionsData, refetch: refetchSuggestions } = useSuggestions(selectedDocument?.id, {
    type: 'SUGGESTION',
  }, {
    enabled: !!selectedDocument?.id,
    staleTime: 0, // Always refetch to avoid caching issues
  })

  // Clear chat history when document changes and refetch suggestions
  useEffect(() => {
    setChatHistory([])
    setChatMessage('')
    // Explicitly refetch suggestions for the new document
    if (selectedDocument?.id) {
      refetchSuggestions()
    }
  }, [selectedDocument?.id, refetchSuggestions])

  // Create suggestion mutation
  const createSuggestionMutation = useCreateSuggestion({
    onSuccess: () => {
      setSuggestionData({ question: '', answer: '', type: 'SUGGESTION' })
      onSuggestionClose()
      refetchSuggestions()
    },
    onError: (error) => {
      console.error('Failed to create suggestion:', error)
    },
  })

  // Get suggestions for the selected document
  const documentSuggestions = useMemo(() => {
    if (!suggestionsData?.data?.suggestions)
      return []
    return suggestionsData.data.suggestions.slice(0, 4) // Show top 4 suggestions
  }, [suggestionsData?.data?.suggestions])

  // Fallback questions when no custom suggestions exist
  const fallbackQuestions = [
    'What is the main topic of this document?',
    'Can you summarize the key points?',
    'What are important details I should know?',
  ]

  const questionsToShow = documentSuggestions.length > 0 ? documentSuggestions : fallbackQuestions.map(q => ({ question: q }))

  const handleAddSuggestion = (question, answer = '') => {
    if (!selectedDocument)
      return

    createSuggestionMutation.mutate({
      knowledgeBaseId: selectedDocument.id,
      data: {
        question: question.trim(),
        answer: answer.trim() || 'This suggestion was added from the playground and needs an answer.',
        type: 'SUGGESTION',
      },
    })
  }

  const handleEditResponse = (aiMessageIndex) => {
    // Find the corresponding user message (should be the one before the AI response)
    const userMessageIndex = aiMessageIndex - 1
    const userMessage = chatHistory[userMessageIndex]
    const aiMessage = chatHistory[aiMessageIndex]

    if (userMessage && userMessage.role === 'user') {
      setSuggestionData({
        question: userMessage.content,
        answer: aiMessage.content,
        type: 'SUGGESTION',
      })
      onSuggestionOpen()
    }
  }

  const handleSaveSuggestion = () => {
    if (!selectedDocument || !suggestionData.question.trim() || !suggestionData.answer.trim()) {
      return
    }

    createSuggestionMutation.mutate({
      knowledgeBaseId: selectedDocument.id,
      data: {
        question: suggestionData.question.trim(),
        answer: suggestionData.answer.trim(),
        type: suggestionData.type,
      },
    })
  }

  return (
    <div className="flex h-full gap-4">
      {/* Document Selector - Left Sidebar */}
      <div className="flex w-80 flex-col rounded-lg border bg-white shadow-sm">
        <div className="border-b p-4">
          <h3 className="text-lg font-semibold text-gray-900">Select Document</h3>
          <p className="mt-1 text-sm text-gray-600">Choose a document from your knowledge base to query</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {knowledgeBases?.vectors?.length === 0
            ? (
                <div className="mt-8 text-center text-gray-500">
                  <FileText className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                  <p>No documents available</p>
                </div>
              )
            : (
                <div className="space-y-2">
                  {knowledgeBases.vectors.map(doc => (
                    <button
                      type="button"
                      key={doc.id}
                      onClick={() => setSelectedDocument(doc)}
                      className={`
                        w-full rounded border p-3 text-left transition-colors
                        ${
                    selectedDocument?.id === doc.id
                      ? 'border-blue-200 bg-blue-50'
                      : `
                        border-gray-200 bg-gray-50
                        hover:bg-gray-100
                      `
                    }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        <FileText className="mt-0.5 h-5 w-5 text-gray-500" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-gray-900">
                            {doc.name || `Document ${doc.id}`}
                          </div>
                          {doc.description && (
                            <div className="mt-1 truncate text-sm text-gray-600">
                              {doc.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
        </div>
      </div>

      {/* Chat Interface - Center */}
      <div className={`
        flex min-h-0 flex-1 flex-col rounded-lg border bg-white shadow-sm
      `}
      >
        {/* Chat Header */}
        <div className="flex-shrink-0 border-b p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Chat with Knowledge Base</h3>
              {selectedDocument && (
                <p className="mt-1 text-sm text-gray-600">
                  Querying:
                  {' '}
                  {selectedDocument.name || `Document ${selectedDocument.id}`}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {chatHistory.length > 0 && (
                <Button
                  size="sm"
                  variant="light"
                  onPress={clearChatHistory}
                  className="flex items-center gap-1"
                  isDisabled={isQuerying}
                >
                  <Trash2 className="h-3 w-3" />
                  Clear Chat
                </Button>
              )}
              {selectedDocument && (
                <Chip size="sm" variant="flat" color="success">
                  Connected
                </Chip>
              )}
            </div>
          </div>
        </div>

        {/* Chat Content */}
        <div className="flex min-h-0 flex-1 flex-col">
          {!selectedDocument
            ? (
                <div className={`
                  flex flex-1 items-center justify-center text-gray-500
                `}
                >
                  <div className="text-center">
                    <FileText className="mx-auto mb-4 h-16 w-16 text-gray-300" />
                    <h3 className="mb-2 text-lg font-medium">Select a Document</h3>
                    <p className="max-w-sm text-center text-sm">
                      Choose a document from your knowledge base to start querying and get AI-powered responses.
                    </p>
                  </div>
                </div>
              )
            : (
                <>
                  {/* AI Response Section */}
                  {/* <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        B
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">Bland AI Assistant</div>
                        <p className="text-sm text-gray-600">
                          Ask questions about your selected document. I'll search through the knowledge base to provide relevant answers.
                        </p>
                      </div>
                    </div>
                  </div> */}

                  {/* Chat History */}
                  <div
                    ref={chatContainerRef}
                    className="min-h-0 flex-1 overflow-y-auto p-4"
                  >
                    {chatHistory.length === 0
                      ? (
                          <div className={`
                            flex h-full min-h-[200px] items-center
                            justify-center text-center text-gray-500
                          `}
                          >
                            <div>
                              <FileText className={`
                                mx-auto mb-3 h-12 w-12 text-gray-300
                              `}
                              />
                              <p>Start asking questions about your document</p>
                            </div>
                          </div>
                        )
                      : (
                          <div className="space-y-4">
                            {chatHistory.map((chat, index) => (
                              <div
                                key={`chat-${chat.role}-${chat.content.substring(0, 50).replace(/\W/g, '')}`}
                                className={`
                                  flex
                                  ${chat.role === 'user'
                                ? `justify-end`
                                : `justify-start`}
                                `}
                              >
                                <div className={`
                                  max-w-lg rounded-lg px-4 py-3
                                  ${
                              chat.role === 'user'
                                ? 'bg-blue-500 text-white'
                                : chat.error
                                  ? `
                                    border border-red-200 bg-red-50 text-red-900
                                  `
                                  : 'bg-gray-100 text-gray-900'
                              }
                                `}
                                >
                                  <div className={`
                                    flex items-start justify-between gap-2
                                  `}
                                  >
                                    <div className={`
                                      flex-1 break-words whitespace-pre-wrap
                                    `}
                                    >
                                      {chat.content}
                                    </div>
                                    {chat.role === 'assistant' && !chat.error && selectedDocument && (
                                      <Button
                                        isIconOnly
                                        size="sm"
                                        variant="light"
                                        onPress={() => handleEditResponse(index)}
                                        className="ml-2 flex-shrink-0"
                                      >
                                        <Edit3 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                  {chat.sources && chat.sources.length > 0 && (
                                    <div className={`
                                      mt-2 border-t border-gray-200 pt-2
                                    `}
                                    >
                                      <div className={`
                                        mb-1 text-xs text-gray-600
                                      `}
                                      >
                                        Sources:
                                      </div>
                                      <div className="space-y-1">
                                        {chat.sources.map((source, _idx) => (
                                          <div
                                            key={`source-${JSON.stringify(source).slice(0, 30).replace(/\W/g, '')}`}
                                            className={`
                                              rounded border bg-white p-2
                                              text-xs break-words
                                            `}
                                          >
                                            {source.content || source.text || JSON.stringify(source)}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            {isQuerying && (
                              <div className="flex justify-start">
                                <div className={`
                                  rounded-lg bg-gray-100 px-4 py-3 text-gray-900
                                `}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`
                                      h-4 w-4 animate-spin rounded-full
                                      border-b-2 border-gray-600
                                    `}
                                    >
                                    </div>
                                    Searching knowledge base...
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                  </div>

                  {/* Suggestions */}
                  <div className="flex-shrink-0 border-t p-4">
                    <div className="mb-3">
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700">
                          {documentSuggestions.length > 0 ? 'Document Suggestions' : 'Quick Questions'}
                        </h4>
                        {selectedDocument && (
                          <Button
                            size="sm"
                            variant="light"
                            onPress={() => handleAddSuggestion(chatMessage || 'New suggestion')}
                            className="flex items-center gap-1 text-xs"
                            isDisabled={!chatMessage.trim() || createSuggestionMutation.isPending}
                            isLoading={createSuggestionMutation.isPending}
                          >
                            <Plus className="h-3 w-3" />
                            Add
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {questionsToShow.map((item, _index) => (
                          <button
                            type="button"
                            key={`question-${item.question.replace(/\W/g, '').toLowerCase()}`}
                            onClick={() => setChatMessage(item.question)}
                            disabled={isQuerying}
                            className={`
                              w-full rounded border bg-gray-50 p-2 text-left
                              text-sm transition-colors
                              hover:bg-gray-100
                              disabled:cursor-not-allowed disabled:opacity-50
                            `}
                          >
                            {item.question}
                          </button>
                        ))}
                      </div>
                      {documentSuggestions.length > 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          These are custom suggestions for this document. Visit the Suggestions tab to manage them.
                        </p>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Ask a question about the selected document..."
                        value={chatMessage}
                        onChange={e => setChatMessage(e.target.value)}
                        className="flex-1"
                        minRows={1}
                        maxRows={3}
                        radius="none"
                        isDisabled={isQuerying}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey && !isQuerying) {
                            e.preventDefault()
                            handleSendMessage()
                          }
                        }}
                      />
                      <Button
                        isIconOnly
                        color="primary"
                        onPress={handleSendMessage}
                        radius="none"
                        className="self-end"
                        isDisabled={!chatMessage.trim() || isQuerying}
                        isLoading={isQuerying}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
        </div>
      </div>

      {/* Suggestion Modal */}
      <SuggestionModal
        isOpen={isSuggestionOpen}
        onClose={onSuggestionClose}
        mode="create"
        suggestionData={suggestionData}
        setSuggestionData={setSuggestionData}
        onSave={handleSaveSuggestion}
        isLoading={createSuggestionMutation.isPending}
        selectedDocument={selectedDocument}
      />
    </div>
  )
}
